import { bloom } from 'three/addons/tsl/display/BloomNode.js';
import { ao } from 'three/addons/tsl/display/GTAONode.js';
import { traa } from 'three/addons/tsl/display/TRAANode.js';
import {
  dot,
  float,
  max,
  min,
  mix,
  normalView,
  output,
  packNormalToRGB,
  pass,
  renderOutput,
  sample,
  screenSize,
  screenUV,
  smoothstep,
  uniform,
  unpackRGBToNormal,
  vec2,
  vec3,
  vec4,
  velocity,
} from 'three/tsl';
import * as THREE from 'three/webgpu';
import type { Atmosphere } from '../atmosphere/Atmosphere';
import type { CloudLayer } from '../atmosphere/CloudLayer';
import { useVolumeDepth } from './lightVolumes';
import { sceneOutputs } from './sceneOutputs';

type N = any;

export interface SurfaceOptions {
  /** Ambient occlusion (GTAO) on nearby surfaces. */
  ao?: boolean;
  /** Velocities for temporal AA. */
  traa?: boolean;
  /** Deferred light volumes (glowing plants and crystals), rendered in a second pass. */
  lights?: THREE.Scene | null;
}

export interface PostOptions {
  atmosphere?: Atmosphere | null;
  clouds?: CloudLayer | null;
  bloom?: boolean;
  traa?: boolean;
  /** Paint pixels that nothing was drawn to in magenta: cracks between pieces of the world. */
  seams?: boolean;
  /**
   * Contrast-adaptive sharpening after TRAA, 0 (off) to 1: wins back the detail that the
   * reduced render scale, TRAA's resolve and the canvas' upscale soften. Needs TRAA.
   */
  sharpen?: number;
}

/** How much of the ambient occlusion to apply (it also darkens direct light and glow). */
const AO_STRENGTH = 0.7;
/** Light volumes brighten the surface they fall on; this much is added outright too, so they
 * still show on near-black ground. */
const LIGHT_ADD = 0.05;

/** The strongest negative lobe RCAS allows (AMD FidelityFX FSR 1). */
const RCAS_LIMIT = 0.25 - 1 / 16;

/**
 * Robust contrast-adaptive sharpening (RCAS, from AMD FidelityFX FSR 1) of a display-space
 * pixel e from its neighbours b (above), d (left), f (right) and h (below). The negative
 * lobe is as strong as it can be without pushing any channel out of 0–1, so it sharpens
 * soft detail and leaves hard edges, which would ring, nearly alone.
 */
function rcas(e: N, b: N, d: N, f: N, h: N, amount: number): N {
  const mn4 = min(min(b, d), min(f, h));
  const mx4 = max(max(b, d), max(f, h));
  const hitMin = min(mn4, e).div(max(mx4.mul(4), 1e-4));
  const hitMax = vec3(1)
    .sub(max(mx4, e))
    .div(min(mn4.mul(4).sub(4), -1e-4));
  const lobeRGB = max(hitMin.negate(), hitMax);
  const lobe = max(-RCAS_LIMIT, min(max(lobeRGB.r, max(lobeRGB.g, lobeRGB.b)), 0)).mul(amount);
  return b.add(d).add(f).add(h).mul(lobe).add(e).div(lobe.mul(4).add(1));
}

/**
 * Tone mapping (the renderer's, with its exposure) and sRGB, then a light display-space
 * grade: a touch more contrast and saturation, and warm highlights.
 */
function display(linear: N): N {
  const c: N = renderOutput(linear).rgb;
  const lum = dot(c, vec3(0.2126, 0.7152, 0.0722));
  const saturated = mix(vec3(lum), c, 1.04);
  const contrasted = saturated.sub(0.5).mul(1.06).add(0.5);
  return contrasted.mul(mix(vec3(1), vec3(1.03, 1.0, 0.96), lum));
}

/**
 * The scene as rendered, before any sky: colour, depth and velocity (and normals for AO),
 * ambient occlusion, and the light volumes. One per game, shared by every sky's pipeline:
 * the scene's shaders are built for this one pass only, so walking into another world
 * (another sky) builds nothing new, and the passes' targets exist once.
 */
export class SceneSurface {
  /** The lit surface (colour with occlusion and light volumes), depth, and velocity. */
  readonly surface: N;
  readonly depth: N;
  readonly velocity: N | null;
  readonly aoNode: N | null = null;
  /** View depths (m) over which ambient occlusion fades out (see fitToWorld). */
  private readonly aoFade = uniform(new THREE.Vector2(120, 450));

  constructor(scene: THREE.Scene, camera: THREE.PerspectiveCamera, o: SurfaceOptions) {
    const scenePass = pass(scene, camera);
    const outputs: Record<string, N> = { output };
    if (o.ao) outputs.normal = packNormalToRGB(normalView);
    if (o.traa) outputs.velocity = velocity;
    if (Object.keys(outputs).length > 1) scenePass.setMRT(sceneOutputs(outputs));
    const color = scenePass.getTextureNode('output');
    this.depth = scenePass.getTextureNode('depth');
    this.velocity = o.traa ? scenePass.getTextureNode('velocity') : null;
    // Light volumes read this depth (the same for every sky).
    useVolumeDepth(scenePass.getTexture('depth'));
    let surface: N = color;
    if (o.ao) {
      scenePass.getTexture('normal').type = THREE.UnsignedByteType;
      const normalTex = scenePass.getTextureNode('normal');
      const aoPass: N = ao(
        this.depth,
        sample((uv: N) => unpackRGBToNormal(normalTex.sample(uv))),
        camera,
      );
      // Half resolution; TRAA smooths the noise. A metre-scale radius grounds grass,
      // trees and rocks without darkening whole valleys.
      aoPass.resolutionScale = 0.5;
      aoPass.radius.value = 1.1;
      aoPass.thickness.value = 1.4;
      aoPass.samples.value = 12;
      this.aoNode = aoPass;
      // Fade out with distance: the far side of the world is kilometres away, where
      // metre-scale occlusion is sub-pixel noise. Reversed-Z depth → view depth.
      const n = uniform(camera.near);
      const f = uniform(camera.far);
      const d = this.depth.sample(screenUV).r;
      const viewDepth = f.mul(n).div(n.add(d.mul(f.sub(n))));
      const near = float(1).sub(smoothstep(this.aoFade.x, this.aoFade.y, viewDepth));
      const occlusion = aoPass.getTextureNode().sample(screenUV).r;
      surface = color.sample(screenUV).mul(mix(float(1), occlusion, near.mul(AO_STRENGTH)));
    }
    if (o.lights) {
      const light = pass(o.lights, camera).getTextureNode().sample(screenUV).rgb;
      // Saturate, so a grove of overlapping lights warms the ground instead of blowing it out.
      const soft = light.div(light.add(1));
      const base = surface.isTextureNode ? surface.sample(screenUV) : surface;
      surface = vec4(base.rgb.mul(vec3(1).add(soft.mul(1.2))).add(soft.mul(LIGHT_ADD)), 1);
    }
    this.surface = surface;
  }

  /**
   * Fit the ambient occlusion's fade to the hollow the camera is in. Its metre-scale
   * occlusion turns to noise a few hundred metres out, and a tiny hollow lies entirely
   * within that range: the fade ends at a third of the radius, but never past 450 m.
   */
  fitToWorld(radius: number): void {
    this.aoFade.value.set(Math.min(120, 0.1 * radius), Math.min(450, 0.35 * radius));
  }
}

/**
 * One sky's frame: the shared scene surface → inverted-atmosphere aerial perspective and
 * clouds → temporal AA → bloom → tone mapping (renderer's exposure) → grade.
 */
export class PostPipeline {
  readonly pipeline: THREE.RenderPipeline;
  readonly bloomNode: N | null = null;
  private readonly traaNode: N | null = null;
  /** When it last drew (ms), and whether its own targets are shrunk (see release). */
  lastUsed = 0;
  private released = false;

  constructor(
    renderer: THREE.WebGPURenderer,
    s: SceneSurface,
    camera: THREE.PerspectiveCamera,
    o: PostOptions,
  ) {
    this.pipeline = new THREE.RenderPipeline(renderer);
    let node: N = o.atmosphere ? o.atmosphere.apply(s.surface, s.depth) : s.surface;
    if (o.atmosphere && o.clouds) node = o.clouds.apply(node, s.depth, o.atmosphere);
    if (o.traa && s.velocity) {
      this.traaNode = traa(node, s.depth, s.velocity, camera);
      node = this.traaNode;
    }
    let glow: N = null;
    if (o.bloom) {
      glow = bloom(node, 0.45, 0.55, 1.6);
      this.bloomNode = glow;
    }
    // Tone map and grade, sharpen, then a subtle vignette. The sharpening works in display
    // space, where RCAS's limits hold: each of the five taps of TRAA's output is mapped
    // (sharing the centre's bloom, which is smooth anyway).
    this.pipeline.outputColorTransform = false;
    const lit = (c: N): N => (glow ? c.add(glow) : c);
    let shown: N;
    const sharpen = o.sharpen ?? 0;
    if (sharpen > 0 && this.traaNode) {
      const tex: N = this.traaNode.getTextureNode();
      const px = vec2(1).div(screenSize);
      const tap = (x: number, y: number): N => display(lit(tex.sample(screenUV.add(px.mul(vec2(x, y))))));
      shown = rcas(tap(0, 0), tap(0, -1), tap(-1, 0), tap(1, 0), tap(0, 1), sharpen);
    } else shown = display(lit(node));
    const d = screenUV.sub(0.5);
    const vignette = float(1).sub(dot(d, d).mul(0.35));
    let out: N = vec4(shown.mul(vignette).clamp(0, 1), 1);
    // The cleared depth is 0 (reversed Z): the background, which a closed world never shows.
    if (o.seams)
      out = s.depth
        .sample(screenUV)
        .r.equal(0)
        .select(vec4(1, 0, 1, 1), out);
    this.pipeline.outputNode = out;
  }

  render(): void {
    this.lastUsed = performance.now();
    this.released = false;
    this.pipeline.render();
  }

  /**
   * Shrink this sky's own targets (TRAA's and bloom's) while another sky is drawn. They grow
   * back by themselves the next time it renders (with fresh TRAA history).
   */
  release(): void {
    if (this.released) return;
    this.released = true;
    const t = this.traaNode;
    if (t) {
      t.setSize(1, 1);
      if (t.beautyNode?.isRTTNode) t.beautyNode.setSize(1, 1);
    }
    this.bloomNode?.setSize(1, 1);
  }
}
